import { Component, Input } from '@angular/core';
import { BasicPieChartConfig } from '@app/data/interface/widget-config';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { EChartsOption } from 'echarts';
import { BaseFilteredEnvironmentalDataChartComponent } from '../base-filtered-environmental-data-chart-component';
import { forceChartOptionsRerender } from '../helpers/widget-render.utils';

@Component({
  selector: 'app-basic-pie-chart',
  templateUrl: './basic-pie-chart.component.html',
  styleUrls: ['./basic-pie-chart.component.scss'],
})
export class BasicPieChartComponent extends BaseFilteredEnvironmentalDataChartComponent {
  @Input() config!: BasicPieChartConfig;
  chartOptions: EChartsOption | null = null;
  id: string = 'basic-pie-chart-x';

  ngOnInit() {
    super.ngOnInit();
    this.id = `basic-pie-chart-${this.config.dataConfig.widgetId}`;
    this.chartTitle = this.config.dataConfig.title || '';
    this.chartSubtitle = this.config.dataConfig.subtitle || '';
    this.domain = this.config.dataConfig.domain || '';
  }

  protected clearChartData(): void {
    this.chartOptions = null;
    this.hasData = false;
  }

  protected setWidgetData(): void {
    let hasSeriesData = false;

    // If we don't have any data sources to look at, exit early.

    const preserveConfiguredLabels = Boolean(this.config?.dataConfig?.preserveConfiguredDataSourceLabels);
    const applicableDataSources = this.selectDataSources(this.config.dataConfig.dataSources, {
      preserveConfiguredLabels,
    });

    // If there's nothing to display....
    if (applicableDataSources.length === 0) {
      this.hasData = false;
      return;
    }
    this.chartOptions = null;
    this.hasData = false;
    const newChartOptions: EChartsOption = this.config.echartsConfig;

    forkJoin(
      applicableDataSources.map((source: any) =>
        this.chartService
          .getEnvironmentalDataForSourceAndAnalyteByDate(
            source.environmentalDataSourceIdentifier,
            source.analyteIdentifier,
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'),
            this.startDate === this.endDate ? 'hour' : 'day'
          )
          .pipe(take(1))
      )
    ).subscribe({
      next: (queryResponses: any[]) => {
        const pieData: { name: string; value: number }[] = [];

        queryResponses.forEach((seriesData: any, index: number) => {
          const source = applicableDataSources[index];
          if (!source) {
            return;
          }
          const dataPoints = seriesData.data?.map((d: any) => d[source.valueField]).filter((v: any) => v != null) || [];

          if (dataPoints.length > 0) {
            hasSeriesData = true;
            const sum = dataPoints.reduce((acc: number, v: number) => acc + v, 0);
            const avg = sum / dataPoints.length;

            pieData.push({
              name:
                source?.label ||
                source?.analyteIdentifier ||
                source?.environmentalDataSourceIdentifier ||
                `Source ${index + 1}`,
              value: +avg.toFixed(2),
            });
          }
        });

        if (!hasSeriesData) {
          this.hasData = false;
          this.chartOptions = {
            color: ['#6AB6B0'],
            title: {
              text: 'No data is available for the selected date range.',
            },
          };
          return;
        }

        newChartOptions.series = [
          {
            type: 'pie',
            center: ['50%', '50%'],
            radius: ['0%', '70%'],
            roseType: 'area',
            itemStyle: {
              borderRadius: 8,
            },
            label: {
              overflow: 'break',
            },
            data: pieData,
          },
        ];

        // Include radius mode if requested
        if (this.config.dataConfig.showBoundary && this.config.dataConfig.boundary) {
          newChartOptions.series.push({
            name: 'Radius Mode',
            type: 'pie',
            center: ['50%', '50%'],
            radius: ['0%', '80%'],
            roseType: 'area',
            itemStyle: {
              borderWidth: 10,
              borderColor: '#f5f5f5', // very light gray
              color: '#fff',
            },
            emphasis: {
              disabled: true,
            },
            z: 0,
            data: [{ value: this.config.dataConfig.boundary }],
          });
        }

        console.log('New chart options:', newChartOptions);

        this.hasData = true;
        this.chartOptions = newChartOptions;
      },
      error: (err: any) => {
        console.error(err);
        this.hasData = false;
        this.chartOptions = {
          color: ['#6AB6B0'],
          title: {
            text: 'No data is available.',
          },
        };
      },
    });
  }

  protected onExpandedStateChanged(expanded: boolean): void {
    super.onExpandedStateChanged(expanded);
    forceChartOptionsRerender(this);
  }
}
